import React, { useCallback, useMemo } from "react";
import { useFormikContext } from "formik";
import { SelectField } from "@td/ui";
import { translate } from "@td/shared_utils";
import { useAdminsSearch } from "../hooks";
import { debounce, compact, uniqBy } from "lodash";
import { fieldTypes } from "../constants";
import { useCurrentUser } from "../contexts/current-user-context";
import { useSuggestions } from "../contexts/suggestions-context";
import { mapAssigneesSuggestionsToOptions } from '../utils/suggestions';

const LOAD_OPTIONS_LIMIT = 8;
const LOAD_OPTIONS_DEBOUNCE_DELAY = 500;

const AssigneeField = React.memo(() => {
  const { values } = useFormikContext();
  const { loadAdmins } = useAdminsSearch();
  const currentUser = useCurrentUser();

  const { assignees } = useSuggestions();
  const suggestedAssigneesOptions = useMemo(
    () => mapAssigneesSuggestionsToOptions(assignees),
    [assignees]
  );

  const currentUserOption = useMemo(() => ({
    value: currentUser.id,
    label: `${translate(null, "care_team_tasks.form.fields.assignee", "me")} (${currentUser.name})`,
  }), [currentUser]);

  const defaultOptions = useMemo(() => {
    return compact(
      uniqBy(
        [currentUserOption, ...suggestedAssigneesOptions, values[fieldTypes.ASSIGNEE]],
        "value"
      )
    );
  }, [values[fieldTypes.ASSIGNEE], currentUserOption]);

  const loadOptions = useCallback(async (inputValue, callback) => {
    const admins = await loadAdmins({
      nameQueryStr: inputValue,
      emailQueryStr: inputValue,
    }, LOAD_OPTIONS_LIMIT);

    callback(admins.map(({ id, name }) => ({ value: parseInt(id), label: name })));
  }, [loadAdmins]);

  const debouncedLoadOptions = useMemo(() => {
    return debounce((inputValue, callback) => {
      loadOptions(inputValue, callback);
    }, LOAD_OPTIONS_DEBOUNCE_DELAY);
  }, [loadOptions]);

  return (
    <SelectField
      async
      isClearable
      defaultOptions={defaultOptions}
      loadOptions={debouncedLoadOptions}
      name={fieldTypes.ASSIGNEE}
      className="assignee-field"
      label={translate(null, "care_team_tasks.form.fields.assignee", "label")}
      placeholder={translate(null, "care_team_tasks.form.fields.assignee", "placeholder")}
    />
  );
});

export default AssigneeField;
